import React, { useEffect, useState, useRef } from 'react';
import { statsData } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import { fetchPublicSettings, makeSectionCopyPicker } from '../services/publicContentService';

const Stats = () => {
  const { language } = useLanguage();
  const isAr = language === 'ar';

  const [sc, setSc] = useState({});
  useEffect(() => {
    let alive = true;
    fetchPublicSettings('section_copy').then((v) => { if (alive) setSc(v || {}); });
    return () => { alive = false; };
  }, []);
  const sec = makeSectionCopyPicker(sc, isAr);
  const heading = sec('stats_heading', isAr ? 'إنجازاتنا' : 'Our Achievements');

  const [items, setItems] = useState(statsData);
  useEffect(() => {
    let alive = true;
    fetchPublicSettings('stats').then((v) => {
      if (!alive) return;
      const list = Array.isArray(v?.items) ? v.items : null;
      if (!list || list.length === 0) return;
      // Normalise to the shape used by the counter renderer.
      const mapped = list.slice(0, 6).map((raw, idx) => ({
        id: idx + 1,
        number: Number(raw.number) || 0,
        prefix:   raw.prefix_ar || raw.prefix   || '',
        prefixEn: raw.prefix_en || raw.prefixEn || raw.prefix || '',
        suffix:   raw.suffix    || '',
        label:    raw.label_ar  || raw.label    || '',
        labelEn:  raw.label_en  || raw.labelEn  || raw.label || '',
      }));
      if (mapped.length > 0) setItems(mapped);
    });
    return () => { alive = false; };
  }, []);

  const [counts, setCounts] = useState(() => items.map(() => 0));
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  // Reset counts when the items list changes (CMS override arrives after fetch).
  useEffect(() => {
    setCounts(items.map(() => 0));
  }, [items]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    const duration = 2000;
    const steps = 60;
    const interval = duration / steps;
    const timers = [];

    items.forEach((stat, index) => {
      let currentStep = 0;
      const increment = stat.number / steps;

      const timer = setInterval(() => {
        currentStep++;
        const newValue = Math.min(Math.floor(increment * currentStep), stat.number);

        setCounts(prev => {
          const newCounts = [...prev];
          newCounts[index] = newValue;
          return newCounts;
        });

        if (currentStep >= steps) {
          clearInterval(timer);
        }
      }, interval);
      timers.push(timer);
    });

    return () => { timers.forEach(clearInterval); };
  }, [isVisible, items]);

  return (
    <section id="stats" ref={sectionRef} className="relative py-24 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-cyan-600 via-blue-600 to-teal-600 dark:from-cyan-900 dark:via-blue-900 dark:to-teal-900" />
      
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }} />
      </div>

      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(15)].map((_, i) => (
          <div
            key={i}
            className="absolute bg-white/10 rounded-full animate-float-slow"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              width: `${20 + Math.random() * 60}px`,
              height: `${20 + Math.random() * 60}px`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${5 + Math.random() * 5}s`
            }}
          />
        ))}
      </div>

      <div className="relative container mx-auto px-4">
        <div className="text-center mb-16 animate-fade-in-up">
          <h2 data-testid="stats-heading" className="text-3xl md:text-4xl lg:text-5xl font-bold text-white">
            {heading}
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto items-stretch">
          {items.map((stat, index) => {
            const prefix = isAr ? stat.prefix : (stat.prefixEn || stat.prefix);
            const label  = isAr ? stat.label  : (stat.labelEn  || stat.label);
            return (
            <div
              key={stat.id}
              className="relative group animate-fade-in-up h-full"
              style={{ animationDelay: `${index * 0.2}s` }}
            >
              <div className="relative h-full min-h-[260px] flex flex-col justify-center
                            bg-white/10 backdrop-blur-md rounded-3xl p-8 
                            border border-white/20 
                            transition-all duration-500 
                            hover:bg-white/20 hover:scale-105 hover:border-white/40
                            shadow-2xl hover:shadow-cyan-500/50">
                
                <div className="text-center space-y-3 flex flex-col items-center justify-center">
                  <div className="text-4xl md:text-5xl font-bold text-white drop-shadow-lg">
                    {prefix}
                    <span className="tabular-nums">
                      {counts[index]}
                    </span>
                    {stat.suffix}
                  </div>

                  {label && (
                    <div className="text-lg md:text-xl font-semibold text-cyan-100 tracking-wide px-2 leading-snug">
                      {label}
                    </div>
                  )}
                </div>

                <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-white/0 via-white/10 to-white/0 
                              opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </div>

              <div className="absolute inset-0 rounded-3xl bg-white/10 blur-xl -z-10 
                            opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Stats;
